/**
 * @file build-card-command.ts
 * @description Shared builder that turns an `ActionCard` into the canonical
 * `GameSlashCommand` consumed by BOTH the Actions panel click path
 * (`triggerCardShortcut`) and slash-command autocomplete (`getAbilityCommands`).
 *
 * Centralising this construction enforces the refactor's North Star: "click
 * + autocomplete share one source of truth" (see
 * `docs/game-engine/ACTION_CARDS.md`). Previously the two
 * paths built commands independently; the autocomplete side emitted bare
 * `use_ability` payloads that the backend denylist rejects for Flurry of
 * Blows, multi-mode Monk/Rogue cards, and Metamagic.
 *
 * The builder returns the command shape only — stateful stage setup
 * (`setStage` / `setSelectedIndex`) stays in `triggerCardShortcut`.
 */

import type { ActionCard } from '@danda/shared';
import type { CompletionStepType, GameSlashCommand } from './game-commands';
import { buildFillText, buildIntentFromCard } from '@/lib/intent/build-from-card';

// Re-export so callers that want the chat-bubble display string (e.g.
// `triggerCardShortcut`'s zero-step path) pull it through this module.
export { buildFillText };

export interface BuildCardCommandOptions {
  /** Current pool-remaining for pool-spend cards (Paladin Lay on Hands).
   *  Surfaced into `meta.poolRemaining` so the pool-spend picker can render
   *  an amount-capped menu at click time. */
  poolRemaining?: number;
}

/** Map a card's `TargetPicker` kind to the matching completion step.
 *  Accepts `string | undefined` so callers don't have to widen or cast;
 *  `self`/`none`/undefined fall to `all_targets`, but callers gate
 *  on `needsTarget` before getting here. `point` (AOE / cone / breath)
 *  maps to `enemies` — the engine treats the picked target_id as the
 *  area center and resolves remaining creatures from the spell shape.
 *  The picker label is overridden upstream to "Center the AoE on…" so
 *  the player understands they're picking a focal point, not a single
 *  target. */
export function targetStepFor(pickerKind: string | undefined): CompletionStepType {
  switch (pickerKind) {
    case 'enemy':
      return 'enemies';
    case 'ally':
      return 'allies';
    case 'ally_or_self':
      return 'allies_or_self';
    case 'dying_ally':
      return 'dying_allies';
    case 'point':
      return 'enemies';
    case 'any_creature':
    case 'multi':
    default:
      return 'all_targets';
  }
}

/** Map a card's activation to the corresponding `intentType` discriminator on
 *  the slash command. Mirrors the `intent_type` on the typed Intent the card
 *  builds (so the slash menu surfaces the right verb), with one exception:
 *  `attack_with_flurry` produces `intent_type: 'attack'` because Flurry runs
 *  through the Attack pipeline, not UseAbility. */
function intentTypeFor(card: ActionCard): string {
  switch (card.activation.kind) {
    case 'use_ability':
      return 'use_ability';
    case 'attack_with_flurry':
      return 'attack';
    case 'spell_cast_with_metamagic':
    case 'cast_spell_from_source':
      return 'cast_spell';
    case 'use_item':
      return 'use_item';
    case 'first_class_intent':
      return card.activation.intent;
  }
}

/**
 * Build the canonical `GameSlashCommand` for `card`, or `null` when the card
 * should not surface as an `/ability`-style command.
 *
 * Returns `null` for:
 *  - `source.kind === 'base_action'` — covered by static `/dodge`, `/dash`, etc.
 *  - `activation.kind === 'use_item'` — covered by `/use` autocomplete.
 *  - `activation.kind === 'first_class_intent'` — covered by `/dodge` etc.
 *
 * **Point-target cards (Burning Hands, Cone of Cold, Spirit Guardians,
 * Breath Weapon, area Turn Undead):** these surface as standard cards.
 * `targetStepFor('point')` maps to `'enemies'` — the engine treats the
 * picked target_id as the area center and resolves the radius/cone from
 * `spell.effect.area`. The picker label is overridden via
 * `meta.targetStageLabel = 'Center the AoE on…'` so the player
 * understands the picked creature is the focal point, not the only
 * target. A true touch / grid-coordinate picker is a follow-up.
 */
export function buildCardSlashCommand(
  card: ActionCard,
  opts: BuildCardCommandOptions = {}
): GameSlashCommand | null {
  if (card.source.kind === 'base_action') return null;
  if (card.activation.kind === 'use_item') return null;
  // Class-ability first-class intents (Rogue Steady Aim, etc.) build a real
  // slash command so the click path mirrors the static `/dodge` entries —
  // base-action cards still short-circuit because the static commands cover
  // them.
  if (card.activation.kind === 'first_class_intent') {
    return buildFirstClassIntentCardCommand(card);
  }
  // Point-target spells (Burning Hands, Cone of Cold, Spirit Guardians,
  // Breath Weapon, etc.) surface as standard cards. The picker maps `point`
  // → `enemies` and labels the prompt "Center the AoE on…" — the engine
  // resolves the radius / cone from the picked center.

  const cmdName = card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  // Magic Initiate / innate-spell cards inherit their parent FEAT's description
  // from the enricher (`magic_initiate_card()` in
  // `backend/shared/src/enricher/mechanics/action_cards/builders.rs:825`), which
  // surfaces "Learn 2 cantrips and 1 1st-level spell from Cleric list" on every
  // per-spell row. For `cast_spell_from_source` cards, render a per-spell line
  // instead — the feat blurb belongs on the feat row, not the spell row.
  //
  // TODO(dup-cleanup): `/cast-<spell>` rows from this builder duplicate the
  // canonical `/<spell>` rows produced by `getSpellCommands` in
  // `game-commands.ts` (around line 1712). The cleaner backend fix is to drop
  // the per-spell card emission entirely in `magic_initiate_card()` and let
  // `collectSpellOptions` Source 2 surface the `/cure-wounds` row from
  // `always_prepared`. Leaving both paths for now — separate change.
  const sourceSpellName =
    card.activation.kind === 'cast_spell_from_source' ? card.activation.spell_name : null;
  const description = sourceSpellName
    ? `Cast ${sourceSpellName}`
    : (card.description ?? `Use ${card.label}`);
  const shortDesc = description.length > 80 ? `${description.slice(0, 77)}...` : description;
  const intentType = intentTypeFor(card);

  if (card.activation.kind === 'spell_cast_with_metamagic') {
    // `all_targets` is deliberate: a metamagic card is a spell modifier, not
    // an ability with a target. Metamagic cards are emitted with
    // `target_picker: None` (see `backend/shared/src/enricher/mechanics/
    // action_cards/builders.rs`); the real target type comes from whichever
    // spell the player picks in the `spells` step. Narrowing the second
    // step here (e.g. to `allies` for Careful Spell) would actively break
    // cases like Fireball-with-Careful-Spell, which still targets enemies.
    const cardBuilt = buildIntentFromCard(card, {});
    // SRD 5.2 Transmuted Spell: caster picks one of Acid/Cold/Fire/Lightning/
    // Poison/Thunder to swap the spell's damage type. Step order is
    // `spells → transmuted_damage_type → all_targets` so the picker
    // dispatcher can read the picked spell's natural damage_type from the
    // accumulator and FILTER the 6-option list to exclude the no-op
    // (Fire→Fire, etc.). Without that filter the player can spend a
    // sorcery point for zero mechanical change.
    //
    // mergeCardIntent overrides the card stub's Fire default with the
    // picked value. Other metamagic options (Careful, Empowered,
    // Quickened, etc.) don't need a sub-pick — only Transmuted.
    const isTransmuted = card.activation.option === 'transmuted_spell';
    const isTwinned = card.activation.option === 'twinned_spell';
    const isCareful = card.activation.option === 'careful_spell';
    // `concentration_confirm` sits immediately after `spells` so the
    // wizard's skip predicate sees the spell pick (which carries
    // `meta.concentrationToReplace` when applicable) as `selectedItem`.
    // The step auto-skips for spells that don't trigger a swap, so this
    // is invisible UX for cantrips / non-concentration spells / re-casts
    // of the same concentration spell.
    let completionSteps: CompletionStepType[];
    let textTemplate: string;
    if (isTransmuted) {
      completionSteps = [
        'spells',
        'concentration_confirm',
        'transmuted_damage_type',
        'all_targets',
      ];
      textTemplate = `Cast {spell} with ${card.label} ({damageType}) on {target}`;
    } else if (isTwinned) {
      // Twinned Spell — primary target then second target. The
      // second-target step bubbles "Can't twin this spell." when the
      // picked spell isn't `twinnable`, so the player learns the
      // restriction without a backend round-trip.
      completionSteps = ['spells', 'concentration_confirm', 'all_targets', 'twinned_target'];
      textTemplate = `Cast {spell} with ${card.label} on {target}`;
    } else if (isCareful) {
      // Careful Spell — exclude-list runs after the primary target so
      // the engine knows which AoE allies the caster wants to shield.
      // The step auto-skips when no allies are caught in the area.
      completionSteps = ['spells', 'concentration_confirm', 'all_targets', 'careful_exclude'];
      textTemplate = `Cast {spell} with ${card.label} on {target}`;
    } else {
      completionSteps = ['spells', 'concentration_confirm', 'all_targets'];
      textTemplate = `Cast {spell} with ${card.label} on {target}`;
    }
    return {
      name: cmdName,
      description: shortDesc,
      intentType,
      category: 'combat',
      roomTypes: ['game'],
      completionSteps,
      textTemplate,
      meta: { cardBuilt, actionCardId: card.id, abilityName: card.label },
    };
  }

  // Multi-mode `use_ability`: default to the first mode.
  // The builder orders "free"/no-cost variants first, so this picks the
  // cheapest mode when one exists. A modal mode picker is a follow-up.
  //
  // Slash-arg override: when there's more than one mode, also
  // surface the full id list as `meta.modeIds`. `resolveFromSubmit` reads
  // this to honor `/patient-defense focused` (vs the default-first
  // `/patient-defense` → `free`) so the web path matches the mobile picker.
  let modeId: string | undefined;
  let modeIds: string[] | undefined;
  if (card.activation.kind === 'use_ability' && card.activation.modes?.length) {
    modeId = card.activation.modes[0]?.mode_id;
    if (card.activation.modes.length > 1) {
      modeIds = card.activation.modes.map((m) => m.mode_id);
    }
  }

  const pickerKind = card.target_picker?.kind;
  // `point` is filtered above, so at this point pickerKind is one of
  // {undefined, self, none, enemy, ally, ally_or_self, dying_ally,
  //  any_creature, multi}.
  const needsTarget = pickerKind != null && pickerKind !== 'self' && pickerKind !== 'none';

  const cardBuilt = buildIntentFromCard(card, { modeId });
  const meta: Record<string, unknown> = {
    cardBuilt,
    actionCardId: card.id,
    abilityName: card.label,
  };
  // Source-cast cards (Magic Initiate, innate-spell grants) have no `spells`
  // step, so the runtime target-audience substitution can't recover the spell
  // name from the selection chain. Stamp it on meta so `resolvePickedSpellName`
  // can fall back to it and curate the target roster (e.g. an innate Fire Bolt
  // card → enemies). Metamagic cards keep their `spells` step, so they don't
  // need this — the backward-walk finds the picked spell there.
  if (sourceSpellName) {
    meta.castSpellName = sourceSpellName;
  }
  if (modeIds) {
    meta.modeIds = modeIds;
  }
  // Point-target spells (Burning Hands, Fireball, Cone of Cold, Breath
  // Weapon, etc.) — re-label the picker so the player understands the
  // picked creature is the area origin, not the only target.
  if (pickerKind === 'point') {
    meta.targetStageLabel = 'Center the AoE on…';
  }

  const req = card.availability?.resource_requirement;
  // NOTE: pool-spend UX is currently Paladin-LoH-only. If another
  // pool-based ability is authored (any `resource` that should surface a
  // `pool_spend` picker), add it here — or preferably promote this to a
  // data-driven flag on `ResourceRequirement` in `backend/shared/src/
  // enricher/mechanics/action_cards/builders.rs`.
  const isPoolCard =
    req?.resource === 'lay_on_hands_pool' && card.activation.kind === 'use_ability';
  if (isPoolCard) {
    meta.poolRemaining = opts.poolRemaining;
    const targetStep: CompletionStepType = needsTarget ? targetStepFor(pickerKind) : 'all_targets';
    return {
      name: cmdName,
      description: shortDesc,
      intentType,
      category: 'combat',
      roomTypes: ['game'],
      completionSteps: ['pool_spend', targetStep],
      textTemplate: `Use ${card.label} to heal {target} for {amount} HP`,
      meta,
    };
  }

  if (!needsTarget) {
    return {
      name: cmdName,
      description: shortDesc,
      fillText: buildFillText(card, { modeId }),
      intentType,
      category: 'combat',
      roomTypes: ['game'],
      meta,
    };
  }

  return {
    name: cmdName,
    description: shortDesc,
    intentType,
    category: 'combat',
    roomTypes: ['game'],
    completionSteps: [targetStepFor(pickerKind)],
    textTemplate: buildFillText(card, { modeId, targetIds: ['{target}'] }),
    meta,
  };
}

/** Build a slash command for a non-base-action `first_class_intent` card
 *  (e.g. Rogue Steady Aim). The resulting command behaves like the static
 *  `/dodge` entries — fills text and submits with the right `intent_type`. */
function buildFirstClassIntentCardCommand(card: ActionCard): GameSlashCommand | null {
  if (card.activation.kind !== 'first_class_intent') return null;
  const cmdName = card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const description = card.description ?? `Use ${card.label}`;
  const shortDesc = description.length > 80 ? `${description.slice(0, 77)}...` : description;
  const intentType = intentTypeFor(card);
  const cardBuilt = buildIntentFromCard(card, {});
  const meta: Record<string, unknown> = {
    cardBuilt,
    actionCardId: card.id,
  };

  const pickerKind = card.target_picker?.kind;
  const needsTarget = pickerKind != null && pickerKind !== 'self' && pickerKind !== 'none';

  if (!needsTarget) {
    return {
      name: cmdName,
      description: shortDesc,
      fillText: buildFillText(card, {}),
      intentType,
      category: 'combat',
      roomTypes: ['game'],
      meta,
    };
  }

  return {
    name: cmdName,
    description: shortDesc,
    intentType,
    category: 'combat',
    roomTypes: ['game'],
    completionSteps: [targetStepFor(pickerKind)],
    textTemplate: buildFillText(card, { targetIds: ['{target}'] }),
    meta,
  };
}
